import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import styled from "styled-components";
import { useLocation, useNavigate, matchPath } from "react-router-dom";
import {
  GBSidebar,
  GBSidebarItem,
  type GBNavItem,
  type GBNavSection,
} from "@greenbackapp/ui";
import { useBusinessAccountingFlag } from "../../hooks/useBusinessDataMode";
import { useBusinessSectionAvailable, usePersonalSideHidden } from "../../hooks/usePersonalSideHidden";
import { useBusinessExperience } from "../../hooks/useBusinessExperience";
import { useBelongsToFirm, usePracticeWorkflow } from "../../hooks/useFirmMembership";
import { useClientEngagement } from "../../hooks/useClientEngagement";
import { useUnreadNotifications } from "../../hooks/useUnreadNotifications";
import {
  getBusinessExperienceState,
  setBusinessExperience,
} from "../../services/businessExperienceStore";
import { businessExperienceService, isRoleExperience } from "../../services/businessExperienceService";
import { useBusinessModules } from "../../hooks/useBusinessModules";
import type { BusinessModulesState } from "../../services/businessModulesStore";
import { moduleRowVisible } from "./moduleNavAccess";
import type { BusinessModuleKey, BusinessNavKey } from "../../types/businessApi";
import {
  spacingTokens,
  typographyTokens,
  borderRadiusTokens,
  shadowTokens,
  breakpointTokens,
  mediaQueries,
  animationTokens,
} from "../../config/designTokens";
import { focusRing } from "../../styles/mixins";
import { Logo } from "./Logo";
import { SubscriptionAttentionLink, SubscriptionBadge } from "../subscription/SubscriptionBadge";
import {
  useSidebar,
  SIDEBAR_WIDTH,
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_MIN_WIDTH_PX,
  SIDEBAR_MAX_WIDTH_PX,
} from "../../contexts/SidebarContext";
import { SYSTEM_BANNER_HEIGHT } from "./SystemBanner";
import { isDemo } from "../../config/demo";
import {
  LayoutDashboard,
  Wallet,
  WalletCards,
  CircleDollarSign,
  HandHeart,
  HeartHandshake,
  TrendingUp,
  Activity,
  GanttChart,
  History,
  FileClock,
  Sun,
  Clock4,
  LocateFixed,
  CarFront,
  ArrowLeftRight,
  Bot,
  Bell,
  Settings,
  CalendarClock,
  FileSignature,
  Wrench,
  FileText,
  Scale,
  ShoppingCart,
  PiggyBank,
  Building2,
  IdCard,
  Landmark,
  Banknote,
  Lock,
  CalendarRange,
  CalendarFold,
  ListChecks,
  ListTodo,
  MailQuestion,
  Inbox,
  Timer,
  Gauge,
  MessagesSquare,
  ChartNoAxesCombined,
  CircleCheckBig,
  Hourglass,
  HeartPulse,
  LayoutPanelTop,
  SquareKanban,
  BookOpen,
  BookText,
  ScrollText,
  Users,
  Receipt,
  ReceiptCent,
  ReceiptText,
  CalendarX2,
  Repeat,
  Repeat2,
  CalendarSync,
  FileSpreadsheet,
  Truck,
  Package,
  Home,
  BadgeDollarSign,
  BarChart3,
  PanelLeftClose,
  PanelLeftOpen,
  Wand2,
  SlidersHorizontal,
  CalendarCheck,
  Eye,
  PieChart,
  type LucideIcon,
  Boxes,
  Briefcase,
  Import,
  Tags,
  Handshake,
  HandCoins,
  Coins,
  Target,
  Cannabis,
  HardHat,
  MapPin,
  UserCog,
  ClipboardCheck,
  PhoneCall,
  ConciergeBell,
  ClipboardList,
  FilePenLine,
  BadgeCheck,
  Layers,
  CircleUserRound,
  Percent,
  ChartColumnBig,
  ChartSpline,
  MapPinned,
  FileCheck2,
  ShoppingBasket,
  Store,
  ScanBarcode,
  ShoppingBag,
  CalendarHeart,
  CalendarDays,
  UsersRound,
  CalendarOff,
  Armchair,
  UserRound,
  Scissors,
  Settings2,
  MessageSquareHeart,
  Lightbulb,
  Heater,
  Stamp,
  Warehouse,
  PackagePlus,
  Caravan,
  ScreenShare,
} from "lucide-react";
import { useClientAttachmentRules } from "../messaging/attachments/attachmentRules";
import {
  canCaptureScreen, captureScreenshot, CaptureCancelled,
} from "../messaging/attachments/screenCapture";
import { handOffScreenshot } from "../messaging/attachments/screenshotHandoff";

interface WebNavigationProps {
  // Props removed as they are now handled by SidebarContext
}

interface NavItemConfig {
  label: string;
  icon?: LucideIcon;
  path: string;
  isExternal?: boolean; // Flag for external links
  children?: NavItemConfig[];
  // Server-declared nav key (PRD 45). Present on the Business rows only —
  // that is the section an experience may subtract from. Path is NOT usable
  // as the key: "Banking" and its "Bank accounts" child share
  // /business/banking, and an experience has to be able to hide one without
  // the other.
  navKey?: BusinessNavKey;
  // Only rendered while practice workflow (PRD 61) is switched on for this
  // deployment. An ITEM flag rather than a section one, because these rows live
  // inside the existing Practice section — which is already gated on belonging
  // to a firm, and must keep rendering for firms on a build without workflow.
  //
  // Absent means off, matching the store: the rows appear when the server
  // starts saying so, and a build where it has not shipped simply has no Work
  // row rather than a dead one.
  workflowOnly?: boolean;
  // Only rendered for somebody who HAS books. `clientOnly` sections draw for a
  // client whose only account is a personal one (PRD 63 §5.2 was written about
  // exactly that person), so a business-scoped row inside one would be a dead
  // link for them. Messages are business-scoped by PRD 68 §5.2's own routes,
  // so the row needs a narrower gate than its section has.
  businessOnly?: boolean;
  // A paid module's row (PRD 88 §4.1): drawn only while the business holds
  // the module, or set it up before a lapse — read-only, never invisible
  // (PRD 42 Rule 4). Read from businessModulesStore, because this component
  // sits above BusinessProvider; UNKNOWN DRAWS NOTHING, the opposite of the
  // experience's rule, since the row adds a paid feature rather than taking
  // away one a customer already has.
  requiresModule?: BusinessModuleKey;
  // The module's own front door — its group and Overview, whose page carries
  // the banner. Drawn wherever a requiresModule row is, and also, once the
  // module is released, for a business that never held it. While a module is
  // unreleased (FIELD_SERVICE_RELEASED false) the two are the same: holders only.
  moduleFrontDoor?: BusinessModuleKey;
  // A group whose own path is one of its children's pages, so with every child
  // hidden — by the experience or a gate — the group has nothing of its own to
  // open and draws nothing, rather than a row leading to a page the person
  // cannot use (a technician's experience names the Field service group
  // before it names any row in it).
  hideWhenEmpty?: boolean;
  // A row this web ships BEFORE the api that serves it: drawn only once the
  // api's experience names its navKey (Show all sends the whole vocabulary,
  // so it counts). Unknown means hidden here — the opposite of navKeyVisible —
  // so nothing new draws until its api is live. PRD 100 Wave 4's Job profitability.
  untilServerLists?: boolean;
}

interface NavSectionConfig {
  title?: string;
  items: NavItemConfig[];
  badge?: string; // Small tag rendered next to the title (e.g. "Preview")
  proOnly?: boolean; // Only rendered for Pro subscribers
  // Only rendered for someone who belongs to an accounting practice
  // (PRD 40 §5.2). The firm equivalent of proOnly, and a separate flag
  // rather than a reuse: a practitioner need not be a Pro subscriber, and a
  // Pro subscriber is usually not a practitioner.
  firmOnly?: boolean;
  // The mirror image: only rendered for someone whose ACCOUNTANT has an
  // engagement covering them (PRD 63 §5.2). Not a third spelling of the two
  // above — a client is usually neither a practitioner nor, now, necessarily a
  // Pro subscriber with books, and this section has to draw for a client whose
  // only account is a personal one.
  clientOnly?: boolean;
}

// Top-level groups mirror the subscription tiers: Personal (Greenback Pro
// today) and Business (preview now, its own subscription later).
// The assistant, defined once because it appears in two places: the Personal
// section, and — when the personal side is hidden (PRD 50) — the Business one.
// Two literals would drift the moment either changed.
const FRANKLIN_ITEM: NavItemConfig = { label: "Franklin", icon: Bot, path: "/chatbot" };

export const NAV_STRUCTURE: NavSectionConfig[] = [
  // Accountant HQ (PRD 40). FIRST, above Personal, because for the people who
  // see it at all this is the primary scope — it is also where "/" now lands
  // them (see LandingRedirect), and a landing page that is not the top section
  // reads as having arrived somewhere by accident.
  //
  // A THIRD TOP-LEVEL SECTION, not rows inside Business. The firm is its own
  // tenant seam: it rides X-Firm-Id, it is resolved separately from
  // X-Business-Id, and these screens list OTHER PEOPLE'S businesses. Filing
  // them under Business would say the practice is a feature of the books,
  // which is backwards — the books are one of many a practice keeps.
  //
  // NO navKey ON ANY ROW. Those belong to the Business vocabulary the server
  // owns (Experiences::Catalog::NAV_KEYS); an experience customises one set of
  // books and has nothing to say about a practice.
  //
  // Every row here is readable by every firm role. staff#index, billing#show
  // and migrations#index carry no require_firm_role! — only the writes do
  // (adding staff, buying seats), and each screen refuses those itself. So
  // there is no role gate to mirror here, and inventing one would advertise
  // fewer rows than the API actually serves.
  {
    title: "Practice",
    firmOnly: true,
    items: [
      { label: "Clients", icon: Briefcase, path: "/firm/clients" },
      // PRD 69 §5. Directly under Clients: "who might become a client" beside
      // "who is". Rollout-gated with the rest of the phase.
      //
      // Handshake, checked against the WHOLE rail: nothing else here shakes
      // hands, and collapsed the headings are gone and only icons remain.
      { label: "Prospects", icon: Handshake, path: "/firm/prospects", workflowOnly: true },
      // PRD 61 §5. Directly under Clients because a practitioner moves between
      // "which client" and "what am I doing for them" all day; Staff and
      // billing are administration and belong below both.
      // SquareKanban rather than ListChecks: the Business section's
      // "Reconcile" already uses ListChecks, and in the collapsed rail — where
      // section headings are gone and only icons remain — a practitioner who
      // also runs their own books would see the same glyph twice. Same trap
      // that made Migrations an Import icon rather than an arrow pair.
      // Below Clients and above Work: a partner opens this on its own, daily,
      // in the last week of a month (PRD 62 §6). CalendarRange is unused
      // elsewhere in the rail — CalendarCheck is Month-End Close's.
      { label: "Close", icon: CalendarRange, path: "/firm/close", workflowOnly: true },
      // How the practice is doing (PRD 66 §4.2). FIRST in the section, and the
      // screen an owner or admin now lands on (§5.1) — the one row that is
      // about the practice rather than about a client.
      //
      // LayoutPanelTop, checked against the WHOLE rail: LayoutDashboard is the
      // personal dashboard's and the two are a genuine pair at a glance, so
      // this takes the panelled variant — and collapsed, the headings are gone
      // and only icons remain. Eighth time this trap has been avoided here.
      { label: "Dashboard", icon: LayoutPanelTop, path: "/firm", workflowOnly: true },
      { label: "Work", icon: SquareKanban, path: "/firm/work", workflowOnly: true },
      // Above Staff because it is the row a practitioner opens every morning,
      // where Staff and billing are administration (PRD 61 §5).
      //
      // ListTodo rather than CalendarCheck: Business/Month-End Close already
      // uses CalendarCheck, and in the collapsed rail — section headings gone,
      // only icons left — a practitioner who also runs their own books would
      // see the same glyph twice. Third time this trap has been avoided here.
      { label: "My week", icon: ListTodo, path: "/firm/my-week", workflowOnly: true },
      // PRD 64 §4. Beside "My week" because both are about a practitioner's own
      // day — that one is what I am doing, this one is what I did — and before
      // Requests, which is about other people.
      //
      // Timer, checked against the WHOLE rail: nothing else here is a clock
      // face (Renewals owns CalendarClock, Month-End Close owns CalendarCheck),
      // and collapsed the headings are gone and only icons remain. Fifth time
      // this trap has been avoided in this file.
      { label: "Time", icon: Timer, path: "/firm/time", workflowOnly: true },
      // Portfolio book health (PRD 66 §4.3). Beside Capacity because both are
      // about the practice as a whole rather than one client's day — and
      // PRD 66 §5 has four more insights reports coming, at which point this
      // row becomes the group they hang under.
      //
      // HeartPulse, checked against the WHOLE rail: Gauge is Capacity's dial
      // and Activity belongs to the personal side; nothing else here is a
      // trace. Collapsed, the headings are gone and only icons remain. Ninth
      // time this trap has been avoided in this file.
      // PRD 66 §5's insights family. A GROUP now that there are three of them,
      // rather than three siblings of the work rows — these are read weekly by
      // whoever runs the practice, where the rows above are opened daily by
      // whoever works in it.
      //
      // The parent repeats its first child's path, like Banking does: a nav
      // item with children loses its own link, so a parent path that is a real
      // page has to appear as a child too or it becomes unreachable.
      //
      // ChartNoAxesCombined for the group, checked against the WHOLE rail:
      // BarChart3 is Business reports, PieChart is Investments, TrendingUp is
      // Performance and Activity is the personal side — and collapsed, the
      // headings are gone and only icons remain. Tenth time this trap has been
      // avoided in this file.
      {
        label: "Insights",
        icon: ChartNoAxesCombined,
        path: "/firm/insights/book-health",
        workflowOnly: true,
        children: [
          { label: "Book health", icon: HeartPulse, path: "/firm/insights/book-health" },
          // CircleCheckBig: ListChecks is Reconcile's, CalendarCheck is
          // Month-End Close's and SquareKanban is the work board's.
          { label: "Work", icon: CircleCheckBig, path: "/firm/insights/work" },
          // Hourglass: Timer belongs to time ENTRY and Gauge to Capacity —
          // this is time already spent.
          { label: "Utilisation", icon: Hourglass, path: "/firm/insights/utilisation" },
        ],
      },
      // PRD 64 §3.6, §4 — beside Time because the two answer the same
      // question from opposite ends: what did we spend, and what is coming.
      //
      // Gauge, checked against the WHOLE rail: no other row is a dial, and
      // collapsed the headings are gone and only icons remain. Sixth time this
      // trap has been avoided in this file.
      { label: "Capacity", icon: Gauge, path: "/firm/capacity", workflowOnly: true },
      // PRD 63 §5.1, and stated there because PRD 40 shipped six waves that a
      // hand-typed URL was the only way to reach. Under the work rows: "what am
      // I waiting on clients for" is a question you ask after looking at the
      // work, not instead of it.
      //
      // MailQuestion, checked against the whole rail: nothing else here is a
      // mail glyph, and collapsed the headings are gone and only icons remain.
      // Client messaging (PRD 68 §5.1). ABOVE Requests, and the pair is
      // deliberate: Requests is what you have ASKED a client to do, Messages is
      // the conversation either side can start. §5.2 keeps the same split on
      // the client's side — "things to do" against "things to read" — so the
      // two must read as siblings here rather than as one feature.
      //
      // MessagesSquare, checked against the WHOLE rail: MailQuestion is
      // Requests', Inbox belongs to the client side and Send is Outreach's —
      // and collapsed, the headings are gone and only icons remain. Eleventh
      // time this trap has been avoided in this file.
      { label: "Messages", icon: MessagesSquare, path: "/firm/messages", workflowOnly: true },
      { label: "Requests", icon: MailQuestion, path: "/firm/requests", workflowOnly: true },
      // What the practice charges ITS CLIENTS (PRD 65 §5), which is a different
      // question from what the practice pays US — that one is a card on
      // Practice Admin, now labelled "Subscription" so the two words stop
      // competing. Fee arrangements, WIP and realization hang off this row.
      //
      // Below Requests because billing is a weekly question where the rows
      // above are daily ones, and above Practice Admin because it is work
      // rather than administration.
      //
      // HandCoins, checked against the WHOLE rail: Banknote is Banking's,
      // Receipt is Bills', ReceiptText is Invoices', Wallet is Accounts' and
      // Scale is the trial balance — collapsed, the headings are gone and only
      // icons remain. Seventh time this trap has been avoided in this file.
      { label: "Billing", icon: HandCoins, path: "/firm/client-billing", workflowOnly: true },
      // ONE ROW FOR THE PRACTICE'S OWN ADMINISTRATION, not three. Staff and
      // billing used to sit here beside Clients and the work rows, which put
      // "who works here" and "what we pay" at the same level as the screens a
      // practitioner opens every morning. They are behind this now, and the
      // rail is shorter for it — which matters most collapsed, where every row
      // costs a glyph a practitioner has to tell apart.
      //
      // IdCard, checked against the WHOLE rail rather than this section: the
      // utility group already owns Settings' cog, Business owns Building2 and
      // Banking owns Landmark — and collapsed, the headings are gone and only
      // icons remain. Fourth time this trap has been avoided here. IdCard also
      // says what the screen is: the practice's own identity, not preferences.
      //
      // NOT owner-gated. The hub behind it carries a card for each of the three
      // screens and every one of them is READABLE by the whole firm — it is
      // only the firm-info form that an admin cannot save, and that page says
      // so itself. Gating the row would take away the only route an admin has
      // to the staff roster.
      { label: "Practice Admin", icon: IdCard, path: "/firm/admin" },
      // Import rather than an arrow pair: ArrowRightLeft is a near-twin of
      // the Personal section's ArrowLeftRight (Transactions), and in the
      // collapsed rail — where the section headings are gone and only icons
      // remain — the two were indistinguishable.
      { label: "Migrations", icon: Import, path: "/firm/migrations" },
    ]
  },
  // What the client's own accountant is waiting on THEM for (PRD 63 §5.2).
  //
  // A PERMANENT ROW, and the reason this section exists at all. The client side
  // of PRD 63 shipped with one door — a dashboard card that renders nothing
  // when nothing is outstanding — so a client who answered everything lost the
  // screen and every request they had ever been sent, and a client with no
  // books never had a dashboard to see the card on. Exactly the failure
  // Accountant HQ shipped with, one layer up.
  //
  // ABOVE Personal and Business because it is somebody else waiting on them,
  // and below Practice because a practitioner who is also a client is a
  // practitioner first.
  //
  // NO navKey, like Practice: those belong to the Business vocabulary the
  // server owns (Experiences::Catalog::NAV_KEYS), and an experience customises
  // one set of books. This is not about books at all.
  {
    title: "Your accountant",
    clientOnly: true,
    items: [
      // Inbox rather than MailQuestion, which the Practice section's own
      // Requests row already owns. A practitioner who is also somebody's
      // client would otherwise see the same glyph twice in the collapsed
      // rail, where the headings are gone and only icons remain — and these
      // two are opposites: one is what you sent out, this is what arrived.
      // PRD 68 §5.2 — beside Requests, and the split the client sees is stated
      // there: "Requests — things to do, with a count that goes down.
      // Messages — things to read, with an unread badge." Adjacent so the two
      // read as siblings; never merged, because one clears by being done and
      // the other by being read.
      //
      // `businessOnly`, unlike its neighbour: messages are business-scoped
      // (§5.2's own routes), and this section draws for a client whose only
      // account is a personal one.
      //
      // MessagesSquare, the same glyph the practice's own Messages row uses —
      // these ARE the same feature from the two ends, and the rail never shows
      // both to one person.
      { label: "Messages", icon: MessagesSquare, path: "/business/messages",
        businessOnly: true },
      { label: "Requests", icon: Inbox, path: "/requests" },
    ]
  },
  {
    title: "Personal",
    items: [
      { label: "Dashboard", icon: LayoutDashboard, path: "/dashboard" },
      { label: "Net Worth", icon: Wallet, path: "/investments/net-worth" },
      // A group's `path` is an active-match prefix, never a destination:
      // toNavItem gives an item with children `children` INSTEAD of `href`, so
      // the parent row opens the dropdown and cannot navigate. /investments has
      // no index route either, so it renders nothing even if you reach it.
      //
      // That left both real screens in here unreachable: the portfolio page and
      // all holdings were in no nav and linked from nowhere else in the app.
      // Every other group that owns a page names it as its first child, the way
      // Banking does with "Bank accounts", so this does the same.
      {
        label: "Investments",
        icon: TrendingUp,
        path: "/investments",
        children: [
          { label: "Portfolio", icon: TrendingUp, path: "/investments/portfolio" },
          { label: "All holdings", icon: PieChart, path: "/investments/all-holdings" },
          { label: "Performance", icon: Activity, path: "/investments/performance" },
        ]
      },
      { label: "Transactions", icon: ArrowLeftRight, path: "/transactions" },
      { label: "Spend", icon: ShoppingCart, path: "/spending" },
      { label: "Budget", icon: PiggyBank, path: "/budget" },
      FRANKLIN_ITEM,
    ]
  },
  {
    title: "Business",
    badge: "Preview",
    proOnly: true,
    items: [
      { label: "Business", icon: Building2, path: "/business", navKey: "business" },
      // Reconcile and Categorization Rules used to sit under Accounting, on the
      // reasoning that reconciliation is an accounting CONTROL rather than a
      // banking chore. That is true about what it means, and it is not what
      // decides where someone looks for it.
      //
      // NAV FOLLOWS THE OBJECT YOU ACT ON. You reconcile a BANK ACCOUNT
      // against a statement — the reconcile overview is a list of bank
      // accounts with their unreconciled counts. Rules fire on BANK
      // TRANSACTIONS, and the moment you want one is just after categorizing
      // a transaction you would rather not categorize again. Period Close is
      // the opposite and stays put: whole business, whole period, no account
      // involved.
      //
      // This also ends a split where /business/banking's own route subtree was
      // exactly these three screens while two of them were advertised
      // elsewhere. Route and nav now agree.
      {
        label: "Banking",
        icon: Landmark,
        path: "/business/banking",
        navKey: "banking",
        children: [
          { label: "Bank accounts", icon: Landmark, path: "/business/banking", navKey: "banking.accounts" },
          { label: "Reconcile", icon: ListChecks, path: "/business/banking/reconcile", navKey: "banking.reconcile" },
          // Processor payouts (PRD 57). Here on the same test as Reconcile:
          // a payout is money landing in a BANK ACCOUNT, and the clearing
          // account it passes through on the way is tied out the same way a
          // bank account is. Directly after Reconcile because it is the same
          // work — explaining a deposit — one step further upstream.
          { label: "Payouts", icon: Banknote, path: "/business/banking/payouts", navKey: "banking.payouts" },
          { label: "Categorization Rules", icon: Wand2, path: "/business/banking/rules", navKey: "banking.rules" },
        ]
      },
      {
        label: "Accounting",
        icon: BookOpen,
        path: "/business/accounting",
        navKey: "accounting",
        children: [
          { label: "Chart of Accounts", icon: BookOpen, path: "/business/accounting/chart-of-accounts", navKey: "accounting.chart_of_accounts" },
          { label: "Journal Entries", icon: ScrollText, path: "/business/accounting/journal-entries", navKey: "accounting.journal_entries" },
          // Sits immediately BEFORE Period Close because that is the order the
          // work happens in: the monthly checklist ends by locking the period
          // (PRD 17 §2.1). Two rows rather than one, because they answer
          // different questions — "what do I still owe this month" versus
          // "what is locked, and when was it unlocked" — and the lock screen
          // is also reached for closes that never ran through a checklist.
          //
          // Month-End Close and Period Close both stay here while Reconcile
          // and the rules move to Banking, and that is the same test applied
          // twice: these two act on the whole business over a whole period,
          // with no account involved.
          // The fixed-asset register (PRD 52). Here rather than under
          // Properties, on the same test the rest of this nav uses AND on
          // §2's argument: the object you act on is an ASSET, and a bakery's
          // oven depreciates. Under Properties it would have said the feature
          // is a landlord's, which is the mistake §2 exists to prevent.
          //
          // After the two registers it sits beside — the chart and the journal
          // — and before the close screens, which is the order the work runs
          // in: assets charge monthly, then the month gets closed.
          { label: "Fixed Assets", icon: Boxes, path: "/business/accounting/fixed-assets", navKey: "accounting.fixed_assets" },
          { label: "Expense Reports", icon: ReceiptCent, path: "/business/accounting/expense-reports", navKey: "accounting.expense_reports" },
          { label: "Store Sales", icon: ShoppingBasket, path: "/business/accounting/store-sales", navKey: "accounting.store_sales" },
          { label: "Month-End Close", icon: CalendarCheck, path: "/business/accounting/month-end-close", navKey: "accounting.month_end_close" },
          { label: "Period Close", icon: Lock, path: "/business/accounting/period-close", navKey: "accounting.period_close" },
        ]
      },
      { label: "Customers", icon: Users, path: "/business/customers", navKey: "customers" },
      // The CRM module (PRD 69 §5). Beside Customers: "who might become a
      // customer" next to "who is". Drawn whether or not the business holds
      // the module — a lapsed module degrades to read-only, never to a hidden
      // row (PRD 42 Rule 4), and the page itself says how to add it.
      //
      // Target, checked against the WHOLE rail: the Practice section's own
      // Prospects row is Handshake, and collapsed the two sections' rows sit in
      // one column of glyphs.
      //
      // "CRM", not "Prospects": the Practice section's row is Prospects, and a
      // practitioner who also runs their own books sees both sections in one
      // rail — two links with the same accessible name is one too many.
      { label: "CRM", icon: Target, path: "/business/crm", navKey: "crm" },
      // Field service (PRD 88 §5.1). After CRM: who might become a customer,
      // who is, then the work done for them. Released (FIELD_SERVICE_RELEASED):
      // every row for a holder, the Overview alone for a business that never
      // held it (fieldServiceNavAccess). Later PRDs
      // add their rows here with `requiresModule: "field_service"`, their keys
      // in NAV_KEYS and the field_service experience in the same PR.
      //
      // HardHat, checked against the WHOLE rail: Wrench is Maintenance's and
      // collapsed the headings are gone and only icons remain. The Overview
      // repeats it, the way Bank accounts repeats Banking's.
      {
        label: "Contractor Services",
        icon: HardHat,
        path: "/business/field",
        navKey: "field",
        moduleFrontDoor: "field_service",
        hideWhenEmpty: true,
        children: [
          { label: "Overview", icon: HardHat, path: "/business/field", navKey: "field.home",
            moduleFrontDoor: "field_service" },
          // PRD 90 §5: the jobs list, right under the Overview — the page the
          // office lives on. ClipboardList, checked against the whole rail:
          // ListChecks and ListTodo are taken, no other row draws a clipboard.
          { label: "Jobs", icon: ClipboardList, path: "/business/field/jobs", navKey: "field.jobs",
            requiresModule: "field_service" },
          // PRD 90 §5 (Wave 3): recurring work outside an agreement, under Jobs,
          // whose jobs it makes. CalendarSync, checked against the whole rail:
          // no other row draws it (Repeat is recurring invoices').
          { label: "Recurring", icon: CalendarSync, path: "/business/field/recurring", navKey: "field.recurring",
            requiresModule: "field_service" },
          // PRD 92 §5: Book a job — the call-booking screen — and the Requests
          // inbox it files from, under Jobs and Recurring, which every booking becomes.
          // PhoneCall and ConciergeBell, checked against the whole rail: no
          // other row draws them (Inbox is the client Requests row's).
          { label: "Book a job", icon: PhoneCall, path: "/business/field/book", navKey: "field.book",
            requiresModule: "field_service" },
          { label: "Requests", icon: ConciergeBell, path: "/business/field/requests", navKey: "field.requests",
            requiresModule: "field_service" },
          // PRD 98 §5: the unsold list — estimates out, drafts, and what was
          // approved. Next to Jobs, which an approval becomes. FilePenLine,
          // checked against the whole rail: no other row draws it
          // (FileSignature is Leases', FileText Bills').
          { label: "Estimates", icon: FilePenLine, path: "/business/field/estimates", navKey: "field.estimates",
            requiresModule: "field_service" },
          // PRD 102 §5: memberships — maintenance plans sold, signed and
          // billed, after the estimates that sometimes sell them. BadgeCheck,
          // checked against the whole rail: no other row draws it. Drawn only
          // when the api's experience names `field.memberships`.
          { label: "Memberships", icon: BadgeCheck, path: "/business/field/memberships", navKey: "field.memberships",
            requiresModule: "field_service" },
          // PRD 110 §5: install projects — jobs grouped and billed in stages,
          // after the memberships. Layers, checked against the whole rail: no
          // other row draws it. Drawn only when the api's experience names
          // `field.projects` (the dispatcher's carries it with no money).
          { label: "Projects", icon: Layers, path: "/business/field/projects", navKey: "field.projects",
            requiresModule: "field_service" },
          // PRD 104 §5: what customers said after each job, every ask, and
          // service recovery. MessageSquareHeart, checked against the whole
          // rail: no other row draws it.
          { label: "Feedback", icon: MessageSquareHeart, path: "/business/field/feedback", navKey: "field.feedback",
            requiresModule: "field_service" },
          // PRD 104 §4.11: work a technician spotted that isn't today's job,
          // waiting for the office to follow up. Lightbulb, checked against the
          // whole rail: no other row draws it.
          { label: "Recommendations", icon: Lightbulb, path: "/business/field/recommendations", navKey: "field.recommendations",
            requiresModule: "field_service" },
          // PRD 91 §5.1: the day board — who is where, and what still needs a
          // day. Under Jobs, the other page the office lives on. GanttChart,
          // checked against the whole rail: no other row draws it
          // (CalendarClock is Renewals').
          { label: "Dispatch", icon: GanttChart, path: "/business/field/dispatch", navKey: "field.dispatch",
            requiresModule: "field_service" },
          // PRD 91 §5.3: the week and month calendars, under the board they
          // open. CalendarFold, checked against the whole rail: no other row
          // draws it (CalendarRange is the firm's Close, CalendarClock Renewals',
          // CalendarCheck Month-End Close).
          { label: "Calendar", icon: CalendarFold, path: "/business/field/calendar", navKey: "field.calendar",
            requiresModule: "field_service" },
          // PRD 100 §5: the billing queue — jobs to invoice, drafts, and what
          // is still owed. After Jobs and Dispatch, where the work it bills lives.
          // ClipboardCheck, checked against the whole rail: no other row draws
          // it, and ReceiptText / Receipt are the core invoice and bill rows.
          { label: "Invoicing", icon: ClipboardCheck, path: "/business/field/invoicing", navKey: "field.invoicing",
            requiresModule: "field_service" },
          // PRD 100 Wave 4: what each job earned, after labour and callbacks.
          // After Invoicing, which makes the revenue it reads. BadgeDollarSign,
          // checked against the whole rail: no other row draws it.
          { label: "Job profitability", icon: BadgeDollarSign, path: "/business/field/reports/jobs", navKey: "field.profitability",
            requiresModule: "field_service", untilServerLists: true },
          // PRD 99 Wave 3: cash and checks taken in the field, and who is
          // owed which tips — after Job profitability, beside the money it
          // reads. A technician's experience lists it too (their own). Coins,
          // checked against the whole rail: no other row draws it (HandCoins
          // is the firm's Billing, Banknote Payouts'). Drawn only once the
          // api's experience names `field.payments`.
          { label: "Payments", icon: Coins, path: "/business/field/payments", navKey: "field.payments",
            requiresModule: "field_service", untilServerLists: true },
          // PRD 97 §5: the price book — tasks, labour rates, fees and parts,
          // with member prices and the markup that builds them. After
          // Invoicing, which bills at these prices. The office's page: in
          // neither field role's nav list (they price through the picker).
          // BookText, checked against the whole rail: BookOpen is the
          // ledger's, Tags is Products & services'.
          { label: "Price book", icon: BookText, path: "/business/field/price-book", navKey: "field.price_book",
            requiresModule: "field_service" },
          // PRD 88 §5.1: every place the business works at. MapPin, checked
          // against the whole rail: no other row draws a pin.
          { label: "Service locations", icon: MapPin, path: "/business/field/locations", navKey: "field.locations",
            requiresModule: "field_service" },
          // PRD 106 §5: the equipment at customers' houses (with the
          // refrigerant log and replacement opportunities as segments), and
          // permits — after Service locations, the places they belong to.
          // Heater and Stamp, checked against the whole rail: no other row
          // draws either. Drawn only once the api names each key.
          { label: "Equipment", icon: Heater, path: "/business/field/equipment", navKey: "field.equipment",
            requiresModule: "field_service", untilServerLists: true },
          { label: "Permits", icon: Stamp, path: "/business/field/permits", navKey: "field.permits",
            requiresModule: "field_service", untilServerLists: true },
          // PRD 103 §5: truck stock (the shop, the vans, transfers, counts,
          // replenishment) and purchasing (POs, receiving, the vendor's
          // bill) — after Permits. Warehouse and PackagePlus, checked against
          // the whole rail: no other row draws either. Drawn only once the
          // api names each key.
          { label: "Stock", icon: Warehouse, path: "/business/field/stock", navKey: "field.stock",
            requiresModule: "field_service", untilServerLists: true },
          { label: "Purchasing", icon: PackagePlus, path: "/business/field/purchasing", navKey: "field.purchasing",
            requiresModule: "field_service", untilServerLists: true },
          // PRD 89 §5: the roster. Time off, vans, skills and zones open from
          // its page header (and the Settings page), so the group stays short
          // on a phone. UserCog, checked against the whole rail: Users is
          // Customers', IdCard is taken.
          { label: "Technicians", icon: UserCog, path: "/business/field/technicians", navKey: "field.technicians",
            requiresModule: "field_service" },
          // PRD 95 §5: the office's day of time — who was on the clock, and
          // what the time went on. History, checked against the whole rail:
          // Timer is the firm's timer, CalendarClock is Renewals'.
          { label: "Time", icon: History, path: "/business/field/time", navKey: "field.time",
            requiresModule: "field_service" },
          // PRD 105 §5: timesheets — each hourly technician's clock for the pay
          // period, signed off and approved, and the payroll export. After
          // Time, the clock it approves. FileClock, checked against the whole
          // rail: no other row draws it.
          { label: "Timesheets", icon: FileClock, path: "/business/field/timesheets", navKey: "field.timesheets",
            requiresModule: "field_service" },
          // PRD 105 Wave 3: commissions — the register, holds, the tie-out and
          // the plans. Owner, accountant and bookkeeper; in neither field
          // role's list (R7). Coins, checked against the whole rail: HandCoins
          // is the firm's Billing, Banknote Banking's. Dark until the api's
          // experience names `field.commissions`.
          { label: "Commissions", icon: Coins, path: "/business/field/commissions", navKey: "field.commissions",
            requiresModule: "field_service", untilServerLists: true },
          // PRD 105 Wave 4: field performance — the KPIs with their counts,
          // never a rank. ChartColumnBig, checked against the whole rail:
          // ChartNoAxesCombined is the firm's analytics group, PieChart the
          // reports'. Dark until the api names `field.performance`.
          { label: "Performance", icon: ChartColumnBig, path: "/business/field/performance", navKey: "field.performance",
            requiresModule: "field_service", untilServerLists: true },
          // PRD 95 §5: a technician's day and week, on a phone. Sun and
          // Clock4, checked against the whole rail: no other row draws them.
          { label: "My day", icon: Sun, path: "/business/field/my-day", navKey: "field.my_day",
            requiresModule: "field_service" },
          { label: "My time", icon: Clock4, path: "/business/field/my-time", navKey: "field.my_time",
            requiresModule: "field_service" },
          // PRD 105 Wave 4: My numbers — a technician's own scorecard,
          // commission statement and plan. ChartSpline, checked against the
          // whole rail: no other row draws it. Dark until the api names
          // `field.my_numbers`.
          { label: "My numbers", icon: ChartSpline, path: "/business/field/my-numbers", navKey: "field.my_numbers",
            requiresModule: "field_service", untilServerLists: true },
          // PRD 96 §5: My location — the employer's notice, my choice, my
          // own history and who looked. LocateFixed, checked against the
          // whole rail: no other row draws it (MapPin is Service locations').
          // Drawn only once the api's experience names `field.my_location`.
          { label: "My location", icon: LocateFixed, path: "/business/field/my-location", navKey: "field.my_location",
            requiresModule: "field_service", untilServerLists: true },
          // PRD 96 §5: the mileage log — trips between jobs and reimbursing
          // them. CarFront, checked against the whole rail: no other row
          // draws it (Truck is Vans'). The dispatcher's too (miles only).
          { label: "Mileage", icon: CarFront, path: "/business/field/mileage", navKey: "field.mileage",
            requiresModule: "field_service", untilServerLists: true },
          // PRD 103 §5: My van — a technician's parts, restocks and counts.
          // Caravan, checked against the whole rail: Truck is Vans', CarFront
          // Mileage's. In field_technician only; dark until the api names it.
          { label: "My van", icon: Caravan, path: "/business/field/my-van", navKey: "field.my_van",
            requiresModule: "field_service", untilServerLists: true },
          // PRD 89 §4.12 (Wave 3): My profile — the technician's own row, and
          // their landing until PRD 95's My day. In field_technician only; an
          // owner or dispatcher with a roster row reaches it from "This is
          // you" on their technician page. CircleUserRound, checked against
          // the whole rail: UserCog is Technicians', Users is Customers'.
          { label: "My profile", icon: CircleUserRound, path: "/business/field/me", navKey: "field.me",
            requiresModule: "field_service" },
        ]
      },
      // Personal services (PRD 121 §4.12, §5.2). After Field service: the
      // other way a business does its work for customers. Dark until
      // PERSONAL_SERVICES_RELEASED: the group draws only for a business
      // holding the module (personalServicesNavAccess); once released, a
      // business that never held it sees the Overview alone. Wave 1 has the
      // Overview only — Settings is reached from it and from Administration,
      // and the Staff, Locations, Menu, My profile and Time off rows join in
      // Waves 2–3 with their pages (PLAN-W1 ruling 3), each in NAV_KEYS and
      // the experiences in the same PR.
      //
      // CalendarHeart, checked against the WHOLE rail: CalendarClock is
      // Renewals', CalendarCheck Month-End Close's, CalendarRange the firm's
      // Close, CalendarSync and CalendarFold field service's.
      {
        label: "Appointments",
        icon: CalendarHeart,
        path: "/business/appointments/overview",
        navKey: "appointments",
        moduleFrontDoor: "personal_services",
        hideWhenEmpty: true,
        children: [
          // PRD 122 §5: the calendar, first. CalendarDays is drawn by no
          // other row (checked against the whole rail).
          { label: "Calendar", icon: CalendarDays, path: "/business/appointments",
            navKey: "appointments.calendar", requiresModule: "personal_services" },
          { label: "Overview", icon: CalendarHeart, path: "/business/appointments/overview",
            navKey: "appointments.overview", moduleFrontDoor: "personal_services" },
          // PRD 122 Wave 5: standing appointments and *Couldn't book* (owner,
          // front desk — ps_front_desk's experience names it, ps_provider's
          // does not). Repeat2 is drawn by no other row (Repeat is recurring
          // invoices', CalendarSync field service's Recurring).
          { label: "Standing", icon: Repeat2, path: "/business/appointments/standing",
            navKey: "appointments.standing", requiresModule: "personal_services" },
          // PRD 126 W1: Checkout (who is waiting; a ticket opens from it or the
          // drawer) and Today's takings (the desk and the books roles, never a
          // provider — the api's experiences decide who sees each). Checked
          // against the whole rail: HandCoins, Banknote, Coins and Wallet are
          // taken; WalletCards and CircleDollarSign are drawn by no other row.
          { label: "Checkout", icon: WalletCards, path: "/business/appointments/checkout",
            navKey: "appointments.checkout", requiresModule: "personal_services" },
          { label: "Takings", icon: CircleDollarSign, path: "/business/appointments/takings",
            navKey: "appointments.takings", requiresModule: "personal_services" },
          // PRD 126 W2: Tips (the books roles; owner and accountant re-split)
          // and My tips (a provider's own — CONTRACT §5: never anyone else's).
          // The api's experiences decide who sees each. HandHeart and
          // HeartHandshake are drawn by no other row (checked against the rail).
          { label: "Tips", icon: HandHeart, path: "/business/appointments/tips",
            navKey: "appointments.tips", requiresModule: "personal_services" },
          { label: "My tips", icon: HeartHandshake, path: "/business/appointments/my-tips",
            navKey: "appointments.my_tips", requiresModule: "personal_services" },
          // Wave 2 (§5.2): the shared roster in this module's words, its time
          // off, the locations, and My profile (a provider's landing until
          // PRD 122's day). Icons checked against the WHOLE rail: UsersRound,
          // CalendarOff, Armchair (MapPin and MapPinned are taken) and
          // UserRound are drawn by no other row.
          { label: "Staff", icon: UsersRound, path: "/business/appointments/staff",
            navKey: "appointments.staff", requiresModule: "personal_services" },
          { label: "Time off", icon: CalendarOff, path: "/business/appointments/time-off",
            navKey: "appointments.time_off", requiresModule: "personal_services" },
          { label: "Locations", icon: Armchair, path: "/business/appointments/locations",
            navKey: "appointments.locations", requiresModule: "personal_services" },
          // Wave 3 (§5.2): the service menu. Scissors is drawn by no other row.
          { label: "Menu", icon: Scissors, path: "/business/appointments/menu",
            navKey: "appointments.menu", requiresModule: "personal_services" },
          { label: "My profile", icon: UserRound, path: "/business/appointments/me",
            navKey: "appointments.me", requiresModule: "personal_services" },
        ]
      },
      // A group rather than a link, because there are now three invoice
      // screens and two of them had no way in at all. The parent of a group
      // is not itself clickable, so "All invoices" carries the link that
      // "Invoices" used to be — losing it would trade one dead end for another.
      {
        label: "Invoices",
        icon: Receipt,
        path: "/business/invoices",
        navKey: "invoices",
        children: [
          { label: "All invoices", icon: Receipt, path: "/business/invoices", navKey: "invoices.all" },
          { label: "Recurring", icon: Repeat, path: "/business/invoices/recurring", navKey: "invoices.recurring" },
          { label: "Credit memos", icon: ReceiptText, path: "/business/credit-memos", navKey: "invoices.credit_memos" },
          // PRD 125 §5: Sales → Cancellation fees (core). Dark like the rest of
          // 125 (moduleFrontDoor: holders until personal services is
          // released, every business after), and shipped BEFORE the api's
          // Experiences::Catalog names `sales.cancellation_fees` — so it draws
          // only once the experience does (untilServerLists). CalendarX2 is
          // drawn by no other row.
          { label: "Cancellation fees", icon: CalendarX2, path: "/business/cancellation-fees",
            navKey: "sales.cancellation_fees", moduleFrontDoor: "personal_services", untilServerLists: true },
          // PRD 101 §4.14 (Wave 3): what is owed each state and the payments
          // made. Under Invoices because the tax was charged on them; the
          // route sits under /business/tax beside 1099 filing.
          // Percent, not Landmark: Banking owns Landmark, and collapsed only
          // icons remain.
          { label: "Sales tax", icon: Percent, path: "/business/tax/sales", navKey: "invoices.sales_tax" },
          // PRD 119 §5.2: the module's nexus dashboard, for a business that
          // holds Sales tax compliance.
          {
            label: "Where you owe",
            icon: MapPinned,
            path: "/business/tax/sales/nexus",
            navKey: "invoices.sales_tax_nexus",
            requiresModule: "sales_tax_compliance",
          },
          // PRD 119 §5.3: the module's returns, beside Where you owe.
          {
            label: "Returns",
            icon: FileCheck2,
            path: "/business/tax/sales/returns",
            navKey: "invoices.sales_tax_returns",
            requiresModule: "sales_tax_compliance",
          },
        ]
      },
      // A group for the same reason Invoices is one. 1099 Filing used to sit
      // under Accounting next to Period Close, as year-end work; it moved here
      // because NAV FOLLOWS THE OBJECT YOU ACT ON (see Banking above). A 1099
      // is built from what was paid to a vendor, and every fix the filing asks
      // for — a missing TIN, a wrong entity type, a W-9 — is made on the vendor
      // record; the page's own breadcrumb already said Vendors. The route stays
      // at /business/tax/1099, so bookmarks and the W-9 links keep working. A
      // grouped parent loses its own link, so "All vendors" carries the one
      // "Vendors" used to be.
      {
        label: "Vendors",
        icon: Truck,
        path: "/business/vendors",
        navKey: "vendors",
        children: [
          { label: "All vendors", icon: Truck, path: "/business/vendors", navKey: "vendors.all" },
          { label: "1099 Filing", icon: FileSpreadsheet, path: "/business/tax/1099", navKey: "vendors.tax_1099" },
        ]
      },
      // A group for the same reason Invoices is one: vendor credits are the
      // payable mirror of credit memos (PRD 34) and belong beside the bills
      // they reduce, not under Vendors. A grouped parent loses its own link,
      // so "All bills" carries the link "Bills" used to be.
      {
        label: "Bills",
        icon: FileText,
        path: "/business/bills",
        navKey: "bills",
        children: [
          { label: "All bills", icon: FileText, path: "/business/bills", navKey: "bills.all" },
          { label: "Vendor credits", icon: ReceiptText, path: "/business/vendor-credits", navKey: "bills.vendor_credits" },
        ]
      },
      // ABOVE Inventory and top-level, not nested under it: inventory is a
      // paid module that a service business may never hold, and the row they
      // can actually use must not be hidden underneath one that is locked or
      // absent (PRD 58 §10.3).
      { label: "Products & services", icon: Tags, path: "/business/service-items", navKey: "service_items" },
      { label: "Inventory", icon: Package, path: "/business/inventory", navKey: "inventory" },
      // The cannabis module (PRD 111 §4.1). ONE row; every cannabis screen is
      // reached from its Overview. Drawn for a business that holds the module,
      // or that set it up and has since lapsed (restricted_industry, which only
      // set-up writes) — never for anyone else, so there is no upsell row (Dave,
      // 2026-10-01: a cannabis business must buy the module). Owner, accountant
      // and bookkeeper only: the api answers every other role 403 (§4.11). The
      // rule is cannabisNavAccess, reached through moduleNavAccess like field
      // service's. The Cannabis glyph is used nowhere else in the rail.
      {
        label: "Cannabis",
        icon: Cannabis,
        path: "/business/cannabis",
        navKey: "cannabis",
        requiresModule: "cannabis",
      },
      // Point of sale (PRD 118). Dark until POS_RELEASED: the group draws only
      // for a business holding the module, so one without the early-access
      // grant finds no Point of sale row at all. Register first — the page a
      // store lives on — then Sales, the back office's tickets, drawers and
      // floor log (Wave 5), then the set-up. A store's register sign-in (the
      // `register` role) sees only Register: its experience names pos and
      // pos.register. Store, ScanBarcode, ShoppingBag and Settings2, checked
      // against the whole rail: no other row draws them (Administration is
      // SlidersHorizontal, Spend's ShoppingCart).
      {
        label: "Point of sale",
        icon: Store,
        path: "/business/pos/register",
        navKey: "pos",
        requiresModule: "pos",
        hideWhenEmpty: true,
        children: [
          { label: "Register", icon: ScanBarcode, path: "/business/pos/register", navKey: "pos.register",
            requiresModule: "pos" },
          { label: "Sales", icon: ShoppingBag, path: "/business/pos/sales", navKey: "pos.sales",
            requiresModule: "pos" },
          { label: "Settings", icon: Settings2, path: "/business/pos/settings", navKey: "pos.settings",
            requiresModule: "pos" },
        ],
      },
      // Same rule as Customers and Inventory: you tag transactions against
      // a property, so it belongs with the other things you act on rather
      // than under Administration. Last before Reports because the reports
      // are where a property tag pays off.
      // Leases hang off Properties for the same reason Vendor credits hang
      // off Bills: it is the object you reach THROUGH the building, and the
      // sidebar is long enough without another top-level row.
      {
        label: "Properties",
        icon: Home,
        path: "/business/properties",
        navKey: "properties",
        children: [
          { label: "All properties", icon: Home, path: "/business/properties", navKey: "properties.all" },
          { label: "Leases", icon: FileSignature, path: "/business/leases", navKey: "properties.leases" },
          { label: "Renewals", icon: CalendarClock, path: "/business/leases/renewals", navKey: "properties.renewals" },
          { label: "Maintenance", icon: Wrench, path: "/business/maintenance", navKey: "properties.maintenance" },
          // PRD 49 §7. Commercial-only, and most landlords will never hold
          // one — but a letter of credit posts NOTHING, so when the daily
          // notice says one is lapsing there is nowhere else in the product to
          // go and look. The page says so plainly when there are none.
          { label: "Letters of credit", icon: Landmark, path: "/business/leases/letters-of-credit", navKey: "properties.letters_of_credit" },
        ]
      },
      { label: "Reports", icon: BarChart3, path: "/business/reports", navKey: "reports" },
      // Not "Settings": the utility row above already carries that label for
      // the personal side, and two identically named rows sent people to the
      // wrong one. This page administers businesses, members and the books,
      // so it is named for that — and takes a non-gear icon to match. The
      // route stays /business/settings; existing links keep working.
      { label: "Administration", icon: SlidersHorizontal, path: "/business/settings", navKey: "administration" },
    ]
  },
];

// Synthetic item keys for rows that are not routes.
const COLLAPSE_KEY = "__collapse";

// The last rows of the scrolling list — reachable, but below everything
// people navigate between. Collapse takes the pinned footer instead, because
// it is a control for the pane itself and has to stay in reach at any scroll.
const legalItems: GBNavItem[] = [
  {
    key: "https://www.greenbackapp.com/privacy/",
    label: "Privacy Policy",
    icon: <FileText size={18} />,
    href: "https://www.greenbackapp.com/privacy/",
    external: true,
    testID: "nav-item-Privacy Policy",
    accessibilityLabel: "Open Privacy Policy in new tab",
  },
  {
    key: "https://www.greenbackapp.com/terms/",
    label: "Terms of Service",
    icon: <Scale size={18} />,
    href: "https://www.greenbackapp.com/terms/",
    external: true,
    testID: "nav-item-Terms of Service",
    accessibilityLabel: "Open Terms of Service in new tab",
  },
];
// "Show all features" (PRD 45 §6.3). THE SAFETY VALVE, and load-bearing
// rather than a courtesy: this sidebar has no search box and no command
// palette, so while an experience hides rows this is the only route back to
// a hidden screen for someone who does not know the URL. It is also the
// reason hiding is safe at all — nothing is ever unreachable, only unlisted.
const SHOW_ALL_KEY = "__show_all";

// "Send a screenshot" (PRD 68 attachments): an ACTION, like "Show all
// features", not a destination. It lives in the nav rather than on the
// Messages page because the screen a client is stuck on is the one in front of
// them: the picture is taken HERE, before navigating, and Messages opens with
// it waiting to be confirmed. From the Messages page, "this tab" would be a
// picture of Messages.
//
// One row, inside "Your accountant", only for somebody who has one, only where
// the browser can capture (most phones cannot), and only once the api says it
// takes files — the same three conditions under which it can actually work.
const SEND_SCREENSHOT_KEY = "__send_screenshot";

// The Business nav keys "Show all" could reveal, read from NAV_STRUCTURE
// itself (plus Trial Balance, which live mode injects) so that adding a row
// cannot leave the valve checking a stale list.
//
// ONLY ROWS THE MODULE GATES WOULD DRAW (PRD 88 §4.1). A Field service row a
// business cannot have is not behind the valve — Show all would not reveal it
// — so it must not count as hidden. That also keeps the valve shut when this
// web runs ahead of an api whose experience lists predate the field keys.
const TRIAL_BALANCE_NAV_KEY: BusinessNavKey = "accounting.trial_balance";

const revealableNavKeys = (items: NavItemConfig[], modules: BusinessModulesState): BusinessNavKey[] =>
  items
    .filter(item => moduleRowVisible(item, modules))
    .flatMap(item => [
      ...(item.navKey ? [item.navKey] : []),
      ...(item.children ? revealableNavKeys(item.children, modules) : []),
    ]);

const businessNavKeys = (modules: BusinessModulesState): BusinessNavKey[] => [
  ...revealableNavKeys(NAV_STRUCTURE.find(section => section.title === "Business")?.items ?? [], modules),
  TRIAL_BALANCE_NAV_KEY,
];

const NavigationContainer = styled.div<{ $isOpen: boolean; $width: string; $resizing: boolean }>`
  width: ${props => props.$width};
  height: calc(100vh - ${SYSTEM_BANNER_HEIGHT});
  background-color: ${props => props.theme.background.paper};
  border-right: 1px solid ${props => props.theme.border.default};
  display: flex;
  flex-direction: column;
  position: fixed;
  left: 0;
  top: ${SYSTEM_BANNER_HEIGHT};
  z-index: 1000;
  transition: ${props => props.$resizing
    ? 'none'
    : `width ${animationTokens.duration.standard} ${animationTokens.easing.easeInOut}, transform 0.3s ease`};

  ${mediaQueries.tablet.small} {
    width: ${SIDEBAR_WIDTH};
    transform: translateX(${props => props.$isOpen ? '0' : '-100%'});
    box-shadow: ${props => props.$isOpen ? shadowTokens.lg : 'none'};
  }
`;

/*
 * The expanded pane's right edge: drag to resize, arrow keys to nudge,
 * double-click to go back to the default. Sits over the border so the hit
 * area is wider than the 1px line people aim at.
 */
const ResizeHandle = styled.div<{ $resizing: boolean }>`
  position: absolute;
  top: 0;
  right: -3px;
  bottom: 0;
  width: 6px;
  cursor: col-resize;
  z-index: 1;
  touch-action: none;
  background-color: ${props => props.$resizing ? props.theme.border.default : 'transparent'};
  transition: background-color ${animationTokens.duration.standard};

  &:hover {
    background-color: ${props => props.theme.border.default};
  }

  &:focus-visible {
    ${focusRing()}
  }

  ${mediaQueries.tablet.small} {
    display: none;
  }
`;

/* The kit sidebar owns the nav list; this hosts it below the logo header. */
const SidebarBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;

  /* The GBSidebar rows are anchors on web — never underline them. */
  a {
    text-decoration: none;
  }

  a:focus-visible,
  [role="button"]:focus-visible {
    ${focusRing()}
    outline-offset: -2px;
  }
`;

const Overlay = styled.div<{ $isVisible: boolean }>`
  display: ${props => props.$isVisible ? 'block' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;

  @media (min-width: ${breakpointTokens.tablet.small}) {
    display: none;
  }
`;

const MobileHeader = styled.div`
  display: none;

  ${mediaQueries.tablet.small} {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: ${spacingTokens.lg};
    background-color: ${props => props.theme.background.paper};
    border-bottom: 1px solid ${props => props.theme.border.default};
    position: fixed;
    top: ${SYSTEM_BANNER_HEIGHT};
    left: 0;
    right: 0;
    z-index: 998;
    height: 60px;
  }
`;

const HamburgerButton = styled.button`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  width: 24px;
  height: 24px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 10;

  &:focus-visible {
    ${focusRing()}
  }

  span {
    width: 24px;
    height: 2px;
    background: ${props => props.theme.text.primary};
    border-radius: ${borderRadiusTokens.full};
    transition: all 0.3s linear;
    position: relative;
    transform-origin: 1px;
  }
`;

const MobileTitle = styled.h2`
  font-family: ${typographyTokens.fontFamily.semiBold};
  font-size: ${typographyTokens.fontSize.subtitle};
  font-weight: ${typographyTokens.fontWeight.semibold};
  color: ${props => props.theme.text.primary};
  margin: 0;
  flex: 1;
  text-align: center;
`;

const Header = styled.div<{ $isCollapsed: boolean }>`
  padding: ${props => props.$isCollapsed ? spacingTokens.lg : spacingTokens.xxl};
  border-bottom: 1px solid ${props => props.theme.border.default};
  background-color: ${props => props.theme.background.paper};
  text-align: center;
  transition: padding ${animationTokens.duration.standard} ${animationTokens.easing.easeInOut};

  ${mediaQueries.mobile.large} {
    padding: ${spacingTokens.lg};
  }
`;

/* Breathing room for the subscription badge inside the kit footer slot. */
const FooterBadgeWrap = styled.div<{ $isCollapsed: boolean }>`
  padding: ${spacingTokens.xs} ${props => props.$isCollapsed ? spacingTokens.xs : spacingTokens.lg} ${spacingTokens.sm};
`;

const MOBILE_QUERY = `(max-width: ${breakpointTokens.tablet.small})`;

/** matchMedia guarded for environments without it (older jsdom, tests). */
const queryMobileViewport = (): MediaQueryList | null => {
  try {
    return typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(MOBILE_QUERY) ?? null
      : null;
  } catch {
    return null;
  }
};

/** True below the tablet.small breakpoint — the slide-in drawer viewport. */
const useIsMobileViewport = (): boolean => {
  const [isMobile, setIsMobile] = useState<boolean>(() => queryMobileViewport()?.matches ?? false);

  useEffect(() => {
    const mql = queryMobileViewport();
    if (!mql) return;
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    if (mql.addEventListener) {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    if (mql.addListener) {
      // Safari < 14 fallback
      mql.addListener(onChange);
      return () => mql.removeListener(onChange);
    }
  }, []);

  return isMobile;
};

export const WebNavigation: React.FC<WebNavigationProps> = () => {
  const {
    isCollapsed,
    toggleSidebar,
    sidebarWidth,
    expandedWidth,
    setExpandedWidth,
    resetExpandedWidth,
    isResizing,
    setIsResizing,
    expandedItems,
    toggleExpandedItem,
    setExpandedItem,
  } = useSidebar();
  // Live business accounting (feature flag). While the flag resolves (null)
  // we render the preview structure — the flag client caches, so this settles
  // immediately on subsequent renders.
  const businessLive = useBusinessAccountingFlag() === true;
  // Is there a Business section at all? The same answer decides whether that
  // section renders AND whether the Personal one may be hidden, because the
  // second rule is only safe if it reads the first (PRD 50 §5).
  const businessAvailable = useBusinessSectionAvailable() === true;
  // Null — still deciding — means show, like every other unknown here.
  const personalHidden = usePersonalSideHidden() === true;
  // The Live sample (PRD 117): no Personal section and no Franklin row.
  const demo = isDemo();
  // The resolved experience for the ACTIVE business (PRD 45). Reached through
  // a module-level store rather than a context because this component is
  // mounted in AppShell — the PARENT route of BusinessShell, where
  // BusinessProvider lives. See businessExperienceStore.
  const { experience } = useBusinessExperience();
  // What the active business holds, through the same kind of store and for the
  // same reason (businessModulesStore).
  const businessModules = useBusinessModules();
  // Is there a practice to show? Unknown draws NOTHING here, which inverts
  // the "unknown means show" rule the experience and personal-side filters
  // use. They subtract from what a customer already has, so guessing wrong
  // hides it; this one adds a section most accounts never have, so guessing
  // wrong offers practice screens to someone with no practice. See
  // useFirmMembership.
  const belongsToFirm = useBelongsToFirm() === true;
  const practiceWorkflow = usePracticeWorkflow() === true;
  // The same unknown-means-nothing rule: this ADDS a section (PRD 63 §5.2).
  const { engaged, outstanding: outstandingRequests } = useClientEngagement();
  const hasAccountant = engaged === true;
  const attachmentRules = useClientAttachmentRules(hasAccountant && businessAvailable);
  const screenshotReady = hasAccountant && businessAvailable && !!attachmentRules &&
    canCaptureScreen();
  const unreadNotifications = useUnreadNotifications();

  // UNKNOWN MEANS SHOW. A null experience covers three real states — the fetch
  // has not settled, there is no active business, and the fetch failed — and in
  // all three every row renders, which is exactly today's behaviour. A menu
  // that hid rows while waiting would flicker on load, and one that hid rows
  // because a request failed would be a support call about missing features.
  const navKeyVisible = useCallback((key: BusinessNavKey | undefined): boolean => {
    if (!key || !experience) return true;
    // A role experience ignores "Show all" (PRD 89 §4.12) — the api already
    // sends it false, and this keeps a stale local true from widening it.
    if (experience.nav_expanded && !isRoleExperience(experience)) return true;
    return experience.nav_keys.includes(key);
  }, [experience]);

  const navStructure = useMemo(() => {
    // Depth-first, keeping canonical order: an experience subtracts from the
    // vocabulary and never rearranges it. A group whose children are all
    // hidden keeps its own row while the group itself is visible, because the
    // parent carries a real route.
    const prune = (items: NavItemConfig[]): NavItemConfig[] =>
      items
        .filter(item => navKeyVisible(item.navKey))
        .map(item => (item.children ? { ...item, children: prune(item.children) } : item));

    // Rollout gates, applied to EVERY section. `prune` above is deliberately
    // Business-only — PRD 45's experiences subtract from that section and no
    // other — and reusing it here would have silently done nothing: it is
    // called from one branch of the map below, the one that handles Business.
    // A test caught exactly that.
    // Module rows (PRD 88 §4.1) gate here too, in preview mode as well as live:
    // with no business there is nothing held, so nothing draws.
    const gate = (items: NavItemConfig[]): NavItemConfig[] =>
      items
        .filter(item => !item.workflowOnly || practiceWorkflow)
        .filter(item => !item.businessOnly || businessAvailable)
        .filter(item => moduleRowVisible(item, businessModules))
        .filter(item => !item.untilServerLists || (!!item.navKey && (experience?.nav_keys.includes(item.navKey) ?? false)))
        .map(item => (item.children ? { ...item, children: gate(item.children) } : item))
        .filter(item => !item.hideWhenEmpty || (item.children?.length ?? 0) > 0);

    return NAV_STRUCTURE
      // The preview's soft proOnly gate is replaced by the flag in live mode.
      .filter(section => !section.proOnly || businessAvailable)
      // Accountant HQ, for the people who have one (PRD 40 §5.2). Default
      // off: the answer has to arrive before the section renders.
      .filter(section => !section.firmOnly || belongsToFirm)
      // And the mirror image, on the same default-off rule (PRD 63 §5.2):
      // offering a client screen to somebody with no accountant is worse than
      // one late frame for somebody who has one.
      .filter(section => !section.clientOnly || hasAccountant)
      // The customer has said they do not use the personal side (PRD 50). The
      // rows go; the routes stay, and Settings — which is in the utility group
      // above, not in this section — is where they turn it back on.
      .filter(section => !(section.title === "Personal" && personalHidden))
      // The Live sample's personal side is hidden by its seed; this is the
      // belt to that seed's braces (PRD 117).
      .filter(section => !(section.title === "Personal" && demo))
      .map(section => {
        if (section.title !== "Business" || !businessLive) return section;
        const items = section.items.map(item => {
          if (item.label !== "Accounting") return item;
          return {
            ...item,
            children: [
              ...(item.children ?? []),
              { label: "Trial Balance", icon: Scale, path: "/business/accounting/trial-balance", navKey: "accounting.trial_balance" },
            ],
          };
        });
        return {
          ...section,
          badge: undefined, // no longer a preview
          items: prune(items),
        };
      })
      // Rollout-gated rows, in every section (PRD 61 §6).
      .map(section => ({ ...section, items: gate(section.items) }))
      // Franklin follows the customer instead of disappearing with the rest of
      // the Personal section. It is the one row there that a business-only
      // customer still wants, and losing the assistant is not what anybody
      // means by "I do not use the personal side".
      //
      // A GUEST IN THIS SECTION, NOT A BUSINESS FEATURE. It keeps its /chatbot
      // route and takes no navKey, so no experience can subtract it and the
      // valve's key list is unchanged — the Business vocabulary is the
      // server's, and this row is not part of it.
      //
      // Its own map rather than the one above, because that one returns early
      // unless live mode is on: a Pro customer still on the preview has a
      // Business section to move Franklin into, and would otherwise lose it.
      // Injected only while the personal side is hidden, so it can never
      // appear twice.
      .map(section => {
        // Not in the Live sample, where Franklin is off (PRD 117, D8).
        if (section.title !== "Business" || !personalHidden || demo) return section;
        return { ...section, items: [ ...section.items, FRANKLIN_ITEM ] };
      });
  }, [businessAvailable, personalHidden, demo, businessLive, navKeyVisible, belongsToFirm, practiceWorkflow, hasAccountant,
      businessModules, experience]);

  // Only offer the valve when something is behind it. `general` resolves to the
  // whole vocabulary, so today this is false, the row does not render, and the
  // sidebar is byte-for-byte what it was.
  //
  // NEVER FOR A ROLE EXPERIENCE (PRD 89 §4.12). A dispatcher or technician is
  // held to the rows their role reaches; behind the valve are the books, each
  // a "This isn't part of your role" page, and saving the choice is refused.
  const hasHiddenNav = useMemo(() => {
    if (!experience || experience.nav_expanded || isRoleExperience(experience)) return false;
    return businessNavKeys(businessModules).some(key => !experience.nav_keys.includes(key));
  }, [experience, businessModules]);

  // Optimistic: the store is updated first so the rows appear on the click,
  // then the preference is persisted. A failure rolls the local change back
  // rather than leaving a lie the next page load would contradict.
  const showAllFeatures = useCallback(async () => {
    const current = getBusinessExperienceState().experience;
    if (!current) return;
    setBusinessExperience({ ...current, nav_expanded: true });
    try {
      setBusinessExperience(await businessExperienceService.update({
        experience_key: current.experience_key,
        customization: current.customization,
        nav_expanded: true,
      }));
    } catch {
      setBusinessExperience(current);
    }
  }, []);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isMobileViewport = useIsMobileViewport();
  // On mobile the sidebar is a full-width drawer — always show labels there.
  const effectiveCollapsed = isCollapsed && !isMobileViewport;

  // Drag the pane's edge. Pointer capture keeps the drag alive when the
  // pointer outruns the 6px handle, which it always does.
  const dragStart = useRef<{ x: number; width: number } | null>(null);
  const handleResizePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragStart.current = { x: e.clientX, width: expandedWidth };
    setIsResizing(true);
  }, [expandedWidth, setIsResizing]);
  const handleResizePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStart.current) return;
    setExpandedWidth(dragStart.current.width + e.clientX - dragStart.current.x);
  }, [setExpandedWidth]);
  const handleResizePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStart.current) return;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    dragStart.current = null;
    setIsResizing(false);
  }, [setIsResizing]);
  const handleResizeKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? 64 : 16;
    if (e.key === "ArrowLeft") setExpandedWidth(expandedWidth - step);
    else if (e.key === "ArrowRight") setExpandedWidth(expandedWidth + step);
    else if (e.key === "Home") setExpandedWidth(SIDEBAR_MIN_WIDTH_PX);
    else if (e.key === "End") setExpandedWidth(SIDEBAR_MAX_WIDTH_PX);
    else return;
    e.preventDefault();
  }, [expandedWidth, setExpandedWidth]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  // Expansion state lives in SidebarContext keyed by item label; the kit keys
  // groups by item key (= route path). pathToLabel bridges the two.
  const pathToLabel = useMemo(() => {
    const map: Record<string, string> = {};
    const walk = (items: NavItemConfig[]) => {
      for (const item of items) {
        map[item.path] = item.label;
        if (item.children) walk(item.children);
      }
    };
    navStructure.forEach(section => walk(section.items));
    return map;
  }, [navStructure]);

  const expandedKeys = useMemo(() => {
    const keys: Record<string, boolean> = {};
    Object.entries(pathToLabel).forEach(([path, label]) => {
      if (expandedItems[label]) keys[path] = true;
    });
    return keys;
  }, [pathToLabel, expandedItems]);

  const toggleGroup = useCallback((label: string) => {
    if (isCollapsed) {
      toggleSidebar();
      // Wait for expansion animation/state update before toggling group
      setTimeout(() => {
        toggleExpandedItem(label);
      }, 100);
    } else {
      toggleExpandedItem(label);
    }
  }, [isCollapsed, toggleSidebar, toggleExpandedItem]);

  // Active item = the deepest configured path matching the current route.
  const activeKey = useMemo(() => {
    let best: string | undefined;
    const visit = (items: NavItemConfig[]) => {
      for (const item of items) {
        if (
          !item.isExternal &&
          matchPath({ path: item.path, end: false }, location.pathname) &&
          (!best || item.path.length > best.length)
        ) {
          best = item.path;
        }
        if (item.children) visit(item.children);
      }
    };
    navStructure.forEach(section => visit(section.items));
    return best;
  }, [navStructure, location.pathname]);

  // Helper recursive find for Mobile Header title
  const currentItem = React.useMemo(() => {
    const find = (items: NavItemConfig[]): NavItemConfig | null => {
      for (const item of items) {
        if (matchPath({ path: item.path, end: false }, location.pathname)) return item;
        if (item.children) {
          const child = find(item.children);
          if (child) return child;
        }
      }
      return null;
    }

    for (const section of NAV_STRUCTURE) {
      const found = find(section.items);
      if (found) return found;
    }
    return null;
  }, [location.pathname]);

  const currentTitle = currentItem?.label || "Dashboard";

  const renderIcon = (Icon: LucideIcon | undefined) => {
    if (!Icon) return null;
    return <Icon size={18} />;
  };

  const toNavItem = useCallback((item: NavItemConfig): GBNavItem => {
    const hasChildren = !!item.children?.length;
    return {
      key: item.path,
      label: item.label,
      icon: renderIcon(item.icon),
      testID: hasChildren ? `nav-group-${item.label}` : `nav-item-${item.label}`,
      accessibilityLabel: item.isExternal
        ? `Open ${item.label} in new tab`
        : hasChildren
          ? item.label
          : `Navigate to ${item.label}`,
      ...(hasChildren
        ? { children: item.children!.map(toNavItem) }
        : { href: item.path, external: item.isExternal }),
    };
  }, []);

  const sections = useMemo<GBNavSection[]>(() => {
    const utilityItems: GBNavItem[] = [];
    // The in-app inbox, for every section: a work reminder, a request answered
    // and a message all land here, and on web nothing else showed them. With
    // the count on the row, because a badge is the only way an inbox that
    // nobody opens on purpose gets opened.
    const notificationsItem = toNavItem({ label: "Notifications", icon: Bell, path: "/notifications" });
    utilityItems.push(
      unreadNotifications > 0 ? { ...notificationsItem, badge: unreadNotifications } : notificationsItem,
    );
    utilityItems.push(toNavItem({ label: "Settings", icon: Settings, path: "/settings" }));

    return [
      { items: utilityItems },
      ...navStructure.map(section => {
        const items = section.items.map(toNavItem);
        // How many things are waiting on them, on the row itself (PRD 63 §5.2)
        // — the one number a client wants without opening anything, and the
        // reason the row is worth its place when it is empty.
        //
        // BY PATH, not by position. This was `items[0]`, which was only ever
        // correct because the section had exactly one row: PRD 68 §5.2 put
        // Messages above Requests and the count silently moved onto it. A
        // request count on a Messages row is the precise confusion §5.2
        // exists to prevent — "things to do" against "things to read".
        if (section.clientOnly && outstandingRequests > 0) {
          // `toNavItem` carries the path through as `key`, not `path`.
          const requestsAt = items.findIndex(item => item.key === "/requests");
          if (requestsAt >= 0) {
            items[requestsAt] = { ...items[requestsAt], badge: outstandingRequests };
          }
        }
        // Last row of the Business section, and only while an experience is
        // actually hiding something (PRD 45 §6.3).
        if (section.title === "Business" && hasHiddenNav) {
          items.push({
            key: SHOW_ALL_KEY,
            label: "Show all features",
            icon: <Eye size={18} />,
            testID: "nav-show-all-features",
            accessibilityLabel: "Show all business features in the menu",
          });
        }
        if (section.clientOnly && screenshotReady) {
          items.push({
            key: SEND_SCREENSHOT_KEY,
            label: "Send a screenshot",
            icon: <ScreenShare size={18} />,
            testID: "nav-send-screenshot",
            accessibilityLabel: "Take a screenshot and send it to your accountant",
          });
        }
        return { label: section.title, badge: section.badge, items };
      }),
      { items: legalItems },
    ];
  }, [navStructure, toNavItem, hasHiddenNav, outstandingRequests, unreadNotifications,
      screenshotReady]);

  // Pinned below the scrolling list; desktop only, since the phone drawer
  // has no collapsed state.
  const collapseItem: GBNavItem | null = isMobileViewport
    ? null
    : {
        key: COLLAPSE_KEY,
        label: isCollapsed ? "Expand" : "Collapse",
        icon: isCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />,
        testID: "sidebar-collapse-toggle",
        accessibilityLabel: isCollapsed ? "Expand sidebar" : "Collapse sidebar",
      };

  // Taken where the person is, then handed to Messages to confirm and send.
  // Closing the picker is not an error; anything else lands on Messages with
  // nothing attached, where they can try again or pick a file.
  const sendScreenshot = useCallback(async () => {
    if (!attachmentRules) return;
    try {
      handOffScreenshot(await captureScreenshot(attachmentRules.max_file_bytes));
    } catch (e) {
      if (e instanceof CaptureCancelled) return;
    }
    navigate("/business/messages");
    setIsOpen(false);
  }, [attachmentRules, navigate]);

  const handleItemPress = useCallback((key: string) => {
    if (key === COLLAPSE_KEY) {
      toggleSidebar();
      return;
    }
    // Reveals the hidden rows in place. Deliberately NOT a navigation: the
    // thing QuickBooks got wrong was making this a trip to Settings to switch
    // views wholesale, and nothing about the user's data or layout changes.
    if (key === SHOW_ALL_KEY) {
      void showAllFeatures();
      return;
    }
    if (key === SEND_SCREENSHOT_KEY) {
      void sendScreenshot();
      return;
    }
    navigate(key);
    setIsOpen(false);
  }, [navigate, toggleSidebar, showAllFeatures, sendScreenshot]);

  const handleToggleExpand = useCallback((key: string) => {
    const label = pathToLabel[key];
    if (label) toggleGroup(label);
  }, [pathToLabel, toggleGroup]);

  // Keyboard support (delegated): arrow up/down walk the rows, right/left
  // expand/collapse group rows. Enter/Space are native on anchors/buttons.
  const navRef = useRef<HTMLDivElement>(null);
  const handleNavKeyDown = useCallback((e: React.KeyboardEvent) => {
    const container = navRef.current;
    if (!container) return;
    const target = e.target as HTMLElement;
    const isGroupRow = target.hasAttribute("aria-expanded");

    switch (e.key) {
      case "ArrowRight":
        if (isGroupRow && target.getAttribute("aria-expanded") === "false") {
          e.preventDefault();
          target.click();
        }
        break;
      case "ArrowLeft":
        if (isGroupRow && target.getAttribute("aria-expanded") === "true") {
          e.preventDefault();
          target.click();
        }
        break;
      case "ArrowDown":
      case "ArrowUp": {
        e.preventDefault();
        const rows = Array.from(
          container.querySelectorAll<HTMLElement>('a[href], [aria-expanded]')
        );
        const index = rows.findIndex(el => el === target);
        if (index === -1) break;
        const nextIndex = e.key === "ArrowDown"
          ? (index + 1 < rows.length ? index + 1 : 0)
          : (index - 1 >= 0 ? index - 1 : rows.length - 1);
        rows[nextIndex].focus();
        break;
      }
    }
  }, []);

  // Auto-expand parent group if child is active
  useEffect(() => {
    const findAndExpand = (items: NavItemConfig[], parentLabel?: string): boolean => {
      for (const item of items) {
        if (matchPath({ path: item.path, end: false }, location.pathname)) {
          if (parentLabel && !expandedItems[parentLabel]) {
            setExpandedItem(parentLabel, true);
          }
          return true; // Found active
        }
        if (item.children) {
          findAndExpand(item.children, item.label);
        }
      }
      return false;
    }

    NAV_STRUCTURE.forEach(section => findAndExpand(section.items));
  }, [location.pathname, expandedItems, setExpandedItem]);

  return (
    <>
      <MobileHeader data-testid="mobile-header">
        <HamburgerButton
          onClick={toggleMenu}
          aria-label="Toggle navigation menu"
          data-testid="mobile-menu-button"
        >
          <span />
          <span />
          <span />
        </HamburgerButton>
        <MobileTitle>{currentTitle}</MobileTitle>
        {/* The badge sits in the closed drawer on a phone: a billing problem
            the person can fix shows here as well (PRD 114 §4.4). */}
        <SubscriptionAttentionLink />
      </MobileHeader>

      <Overlay
        $isVisible={isOpen}
        onClick={() => setIsOpen(false)}
        data-testid="sidebar-overlay"
      />

      <NavigationContainer
        $isOpen={isOpen}
        $width={sidebarWidth}
        $resizing={isResizing}
        data-testid="navigation-container"
      >
        {!effectiveCollapsed && !isMobileViewport && (
          <ResizeHandle
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize navigation"
            aria-valuenow={expandedWidth}
            aria-valuemin={SIDEBAR_MIN_WIDTH_PX}
            aria-valuemax={SIDEBAR_MAX_WIDTH_PX}
            tabIndex={0}
            title="Drag to resize · double-click to reset"
            data-testid="sidebar-resize-handle"
            $resizing={isResizing}
            onPointerDown={handleResizePointerDown}
            onPointerMove={handleResizePointerMove}
            onPointerUp={handleResizePointerUp}
            onPointerCancel={handleResizePointerUp}
            onDoubleClick={resetExpandedWidth}
            onKeyDown={handleResizeKeyDown}
          />
        )}
        <Header $isCollapsed={effectiveCollapsed}>
          <Logo
            variant={effectiveCollapsed ? "minimal" : "text"}
            size={effectiveCollapsed ? "medium" : "large"}
          />
        </Header>

        <SidebarBody role="navigation" ref={navRef} onKeyDown={handleNavKeyDown}>
          <GBSidebar
            sections={sections}
            activeKey={activeKey}
            onItemPress={handleItemPress}
            collapsed={effectiveCollapsed}
            expandedKeys={expandedKeys}
            onToggleExpand={handleToggleExpand}
            width={effectiveCollapsed ? parseInt(SIDEBAR_COLLAPSED_WIDTH, 10) : expandedWidth}
            bordered={false}
            transparent
            footer={
              <>
                <FooterBadgeWrap $isCollapsed={effectiveCollapsed}>
                  <SubscriptionBadge isCollapsed={effectiveCollapsed} />
                </FooterBadgeWrap>
                {collapseItem && (
                  <GBSidebarItem
                    item={collapseItem}
                    collapsed={effectiveCollapsed}
                    onItemPress={handleItemPress}
                  />
                )}
              </>
            }
          />
        </SidebarBody>
      </NavigationContainer>
    </>
  );
};
